import { useCallback, useEffect } from "react";
import { useApp } from "@/store/app";
import { useAuth } from "@/store/auth";
import { cloudCode } from "@/modules/cloud";
import { commEmitter, dataSyncEmitter } from "@/modules/events/emitter";
import { signOut } from "@/modules/cloud/auth";
import { messages } from "@/modules/communication";
import externalLinks from "@/modules/externalLinks";
import clearSession from "../app/clearSession";

let isRecoveringSession = false;

export function useStationReset() {
  const setStatus = useApp((state) => state.setStatus);

  const resetStation = useCallback(
    async ({ signOutFromCloud = true }: { signOutFromCloud?: boolean } = {}) => {
      setStatus("SIGNING_OUT");
      try {
        if (signOutFromCloud) await signOut();
        clearSession();
        window.location.replace(externalLinks.login);
      } catch (err) {
        log.err(err);
      }
    },
    [setStatus]
  );

  const destroyDevice = useCallback(async () => {
    log.app("device was unpaired from the account");
    await resetStation();
  }, [resetStation]);

  const handleInvalidSession = useCallback(async (failedToken?: string) => {
    if (isRecoveringSession) return;
    isRecoveringSession = true;
    log.auth("sessionToken was invalid, checking for a newer web session");

    try {
      const storedUser = window.localStorage.getItem("parseUser");
      const webUser: User | null = storedUser ? JSON.parse(storedUser) : null;

      if (webUser?.sessionToken && webUser.sessionToken !== failedToken) {
        const validUser: User | null = await cloudCode.run(
          "me",
          { _method: "GET", _SessionToken: webUser.sessionToken },
          { path: "users" }
        );
        if (validUser) {
          log.auth("Recovered newer web session, reloading");
          useAuth.getState().setUser({ ...validUser, sessionToken: webUser.sessionToken });
          window.location.reload();
          return;
        }
      }
      await resetStation({ signOutFromCloud: false });
    } catch (err) {
      log.err("Session recovery failed", err);
      await resetStation({ signOutFromCloud: false });
    } finally {
      isRecoveringSession = false;
    }
  }, [resetStation]);

  useEffect(() => {
    commEmitter.on(messages.DeviceUnpairedMessage.name, destroyDevice);
    dataSyncEmitter.on("invalid-session", handleInvalidSession);
    dataSyncEmitter.on("device-not-paired", destroyDevice);
    return () => {
      commEmitter.off(messages.DeviceUnpairedMessage.name, destroyDevice);
      dataSyncEmitter.off("invalid-session", handleInvalidSession);
      dataSyncEmitter.off("device-not-paired", destroyDevice);
    };
  }, [destroyDevice, handleInvalidSession]);

  return { resetStation };
}
